/**
 * El idioma de la interfaz: español o inglés. Se pone solo según el navegador (español si el navegador está en
 * cualquier español, inglés en todo lo demás) y el usuario lo puede cambiar con el selector; la elección se recuerda.
 *
 * Los textos se escriben en español, como siempre, envueltos en `t()`: el español es la clave y la traducción
 * vive en `en/*.ts`. Si falta una traducción se ve el español, nunca un hueco (y `idioma.test.ts` avisa).
 *
 * Esto es solo la interfaz. El idioma del contenido que genera IGOR (estrategia, textos, guiones) es de cada marca.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Idioma = 'es' | 'en';
export type Variables = Record<string, string | number>;

const CLAVE = 'igor-idioma';

/** Todas las traducciones al inglés, juntando los archivos de `en/` (uno por grupo de pantallas). */
const EN: Record<string, string> = Object.assign(
  {},
  ...Object.values(import.meta.glob<{ default: Record<string, string> }>('./en/*.ts', { eager: true })).map((m) => m.default),
);

export function idiomaDelNavegador(): Idioma {
  const lista = navigator.languages?.length ? navigator.languages : [navigator.language];
  // Manda el idioma preferido del navegador (es-CO, es-MX, es-ES… → español; cualquier otro → inglés).
  return lista[0]?.toLowerCase().startsWith('es') ? 'es' : 'en';
}

function idiomaInicial(): Idioma {
  try {
    const guardado = localStorage.getItem(CLAVE);
    if (guardado === 'es' || guardado === 'en') return guardado;
  } catch {
    // Sin almacenamiento (navegación privada): se usa el del navegador.
  }
  return idiomaDelNavegador();
}

/** El idioma en curso, para el código que no es un componente (la API lo manda al motor en cada petición). */
let actual: Idioma = typeof navigator === 'undefined' ? 'es' : idiomaInicial();
export const idiomaActual = () => actual;

/** Rellena `{nombre}` con sus valores. */
const rellenar = (texto: string, vars?: Variables) =>
  vars ? texto.replace(/\{(\w+)\}/g, (todo, k: string) => (k in vars ? String(vars[k]) : todo)) : texto;

/** Traduce en el idioma en curso. Para los componentes, mejor `useIdioma().t`, que se repinta al cambiarlo. */
export function traducir(texto: string, vars?: Variables, idioma: Idioma = actual): string {
  return rellenar(idioma === 'en' ? (EN[texto] ?? texto) : texto, vars);
}

/** Para las pruebas: ¿hay traducción de este texto? */
export const tieneTraduccion = (texto: string) => texto in EN;

type Contexto = {
  idioma: Idioma;
  cambiar: (i: Idioma) => void;
  t: (texto: string, vars?: Variables) => string;
  /** Para fechas y números: `es-CO` o `en-US`. */
  locale: string;
};

const Ctx = createContext<Contexto>({ idioma: 'es', cambiar: () => {}, t: (x, v) => rellenar(x, v), locale: 'es-CO' });

export function ProveedorDeIdioma({ children }: { children: ReactNode }) {
  const [idioma, setIdioma] = useState<Idioma>(idiomaInicial);

  useEffect(() => {
    actual = idioma;
    document.documentElement.lang = idioma;
    try {
      localStorage.setItem(CLAVE, idioma);
    } catch {
      // Si no se puede guardar, el cambio dura lo que dure la visita.
    }
  }, [idioma]);

  const t = useCallback((texto: string, vars?: Variables) => traducir(texto, vars, idioma), [idioma]);
  const valor = useMemo(() => ({ idioma, cambiar: setIdioma, t, locale: idioma === 'en' ? 'en-US' : 'es-CO' }), [idioma, t]);
  // `actual` se pone ya al pintar, no después: los hijos que llaman a la API en su primer efecto lo necesitan.
  actual = idioma;
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export const useIdioma = () => useContext(Ctx);

/** El selector ES / EN, para ponerlo junto al botón de tema. */
export function SelectorDeIdioma() {
  const { idioma, cambiar, t } = useIdioma();
  const otro: Idioma = idioma === 'es' ? 'en' : 'es';
  return (
    <button
      className="sel-mes sel-idioma"
      onClick={() => cambiar(otro)}
      title={t('Cambiar a {idioma}', { idioma: otro === 'en' ? 'English' : 'Español' })}
      aria-label={t('Cambiar a {idioma}', { idioma: otro === 'en' ? 'English' : 'Español' })}
    >
      {idioma.toUpperCase()}
    </button>
  );
}
